/**
 * Onboarding — step 2 of 3: your agency (Reppr Elite access shell, phase 7).
 *
 * ONE path now: join an existing agency with an invitation code.
 *
 * Creating an agency from here is gone. The page used to create one AND set the
 * caller's own `role` and `agency_id` in the same breath, which is precisely the
 * self-escalation the row rules were changed to stop — a user who can set their
 * own role can set it to "superadmin", and every rule grants that role every
 * agency's data. `agencies.createRule` is `null`, so the old path could only
 * have ended in a confusing error anyway.
 *
 * Agencies now come from exactly two server-side places: `api/auto-provision.js`
 * when someone pays, and `api/create-agency.js` when an operator makes one
 * deliberately.
 *
 * Joining used to list every agency and let the user attach themselves. The
 * agencies rules already return an empty list to a user with no agency, so that
 * was broken end to end and was never a sanctioned join. It is replaced by
 * invitation-code entry through the accept-invite endpoint, which validates the
 * code, the invited email and the 7-day expiry server-side.
 */

import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { pb } from '../lib/pocketbase'
import { useSession } from '../hooks/useSession'
import { seedDemoData } from '../utils/demoData'
import { acceptInvitationByToken } from '../api/invitations'
import { applyPendingSubscription } from '../api/subscriptions'
import { getSupportMailto } from '../lib/config'
import AccessShell, { AccessField, AccessBanner, AccessCtas, AccessFoot } from '../components/access/AccessShell'
import styles from '../components/access/AccessShell.module.css'

/** Invite code kept across the sign-in round trip. Read by Login as well. */
export const PENDING_INVITE_KEY = 'reppr_pending_invite'
/** Set by Register when the paid subscription could not be attached. */
export const PENDING_SUB_FAILED_KEY = 'reppr_pending_sub_failed'


const STEPS = [
  { num: '1', label: 'Account', hint: 'Who you are' },
  { num: '2', label: 'Your agency', hint: 'Join with an invitation' },
  { num: '3', label: 'Your book', hint: 'Athletes, contracts and the desk' },
]

function StepIndicator({ activeIndex }: { activeIndex: number }) {
  return (
    <div className={styles.steps}>
      {STEPS.map((s, i) => {
        const done = i < activeIndex
        const active = i === activeIndex
        return (
          <div key={s.num} className={`${styles.step} ${active ? styles.stepActive : ''}`}>
            <div className={styles.stepTop}>
              <span className={`${styles.stepNum} ${done ? styles.stepNumDone : active ? styles.stepNumActive : ''}`}>
                {done ? '✓' : s.num}
              </span>
              <span className={`${styles.stepLabel} ${done ? styles.stepLabelDone : active ? styles.stepLabelActive : ''}`}>
                {s.label}
              </span>
            </div>
            <div className={styles.stepHint}>{s.hint}</div>
          </div>
        )
      })}
    </div>
  )
}

export default function Onboarding() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { user, refreshUser } = useSession()
  const userName = (user as any)?.name as string | undefined

  const [inviteCode, setInviteCode] = useState(
    searchParams.get('invite') ?? sessionStorage.getItem(PENDING_INVITE_KEY) ?? '',
  )
  // A paid account whose agency link failed at registration lands here with
  // no invitation to paste. Give them the retry, not a dead end.
  const [subscriptionFailed] = useState(() => sessionStorage.getItem(PENDING_SUB_FAILED_KEY) === '1')
  const [retryingSub, setRetryingSub] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [welcome, setWelcome] = useState<{ agencyName: string; agencyId: string | null; joined: boolean } | null>(null)
  const [seedingDemo, setSeedingDemo] = useState(false)

  useEffect(() => {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userName])

  // Already in an agency → nothing to onboard.
  useEffect(() => {
    if (user && !loading && !welcome) {
      if ((user as any)?.agency_id) navigate('/dashboard', { replace: true })
    }
  }, [user, loading, welcome, navigate])

  async function handleJoin(e: React.FormEvent) {
    e.preventDefault()
    if (!inviteCode.trim()) {
      setError('Paste the invitation code from your email.')
      return
    }
    if (loading) return
    setLoading(true)
    setError(null)
    try {
      const result = await acceptInvitationByToken(inviteCode.trim())
      sessionStorage.removeItem(PENDING_INVITE_KEY)
      // agency_id only comes back on an auth response (see Login), so the
      // session is refreshed with the server, not re-read from getOne.
      await pb.collection('users').authRefresh()
      await refreshUser()
      setWelcome({ agencyName: result.agencyName ?? 'your agency', agencyId: result.agencyId, joined: true })
    } catch (err: any) {
      setError(err.message || 'Failed to accept the invitation')
    } finally {
      setLoading(false)
    }
  }

  async function handleExploreSampleData() {
    if (!user || !welcome?.agencyId) return
    setSeedingDemo(true)
    try {
      await seedDemoData(welcome.agencyId, user.id)
      navigate('/dashboard')
    } catch {
      setSeedingDemo(false)
      setError('Failed to load sample data. Your agency exists; you can still start fresh.')
    }
  }

  // This route is behind sign-in; a signed-out invitee never reaches it. The
  // invite link goes to /login, which keeps the code and comes back here.
  if (!user) return null

  // ── Step 3 entry: agency exists, choose how to start ─────────────
  if (welcome) {
    return (
      <AccessShell
        eyebrow="Step 3 of 3 · Your book"
        title={welcome.joined ? `You’re part of ${welcome.agencyName}.` : `${welcome.agencyName} exists.`}
        blurb={welcome.joined
          ? 'Your seat is active. Head to the desk.'
          : 'Sample data shows every screen working before you add your own records. It can be cleared later.'}
        width={560}
      >
        <StepIndicator activeIndex={2} />
        {error && <div className={styles.error}>{error}</div>}
        {welcome.joined ? (
          <AccessCtas
            primary="Open the desk"
            primaryType="button"
            onPrimary={() => navigate('/dashboard')}
          />
        ) : (
          <>
            <AccessCtas
              primary={seedingDemo ? 'Loading sample data…' : 'Explore with sample data'}
              primaryType="button"
              onPrimary={handleExploreSampleData}
              disabled={seedingDemo}
              secondary={
                <button className={styles.secondary} onClick={() => navigate('/dashboard')} disabled={seedingDemo}>
                  Start fresh instead
                </button>
              }
            />
            <AccessFoot>
              A small fictional roster. Never billed, never visible outside your agency.
            </AccessFoot>
          </>
        )}
      </AccessShell>
    )
  }

  // ── Step 2: create or join ───────────────────────────────────────
  return (
    <AccessShell
      eyebrow="Step 2 of 3 · Your agency"
      title="Join your team."
      blurb={'Paste the invitation code from your admin’s email. If your agency has turned on team sign-in, signing in with Google on your company email adds you without one.'}
      width={560}
    >
      <StepIndicator activeIndex={1} />



      {(
        <form onSubmit={handleJoin} noValidate>
          {subscriptionFailed && (
            <AccessBanner
              title="Paid, but not attached to your agency yet"
              body="Your payment went through and the account exists; the link between them failed. Retry below, or write to support with the email you paid with."
            />
          )}
          {subscriptionFailed && (
            <AccessCtas
              primary={retryingSub ? 'Retrying…' : 'Retry linking my subscription'}
              primaryType="button"
              disabled={retryingSub}
              onPrimary={async () => {
                setRetryingSub(true)
                setError(null)
                try {
                  const result = await applyPendingSubscription(user.id, (user as any).email)
                  if (!result.agencyId) throw new Error('No agency came back for this payment.')
                  sessionStorage.removeItem(PENDING_SUB_FAILED_KEY)
                  await refreshUser()
                  navigate('/dashboard', { replace: true })
                } catch (err: any) {
                  setError(`${err.message || 'Retry failed'}. Support: ${getSupportMailto('Paid but no agency')}`)
                } finally {
                  setRetryingSub(false)
                }
              }}
            />
          )}
          <AccessBanner
            title="Codes are per person and last seven days"
            body="If yours has expired, ask your admin for a new invitation."
          />
          <AccessField label="Invitation code" htmlFor="ob-code"
            hint="The part after “invite=” in the link also works.">
            <input
              id="ob-code"
              type="text"
              value={inviteCode}
              onChange={e => setInviteCode(e.target.value)}
              placeholder="Paste the code"
              autoComplete="off"
              autoFocus
            />
          </AccessField>
          {error && <div className={styles.error}>{error}</div>}
          <AccessCtas
            primary={loading ? 'Checking…' : 'Join agency'}
            disabled={loading || !inviteCode.trim()}
          />
        </form>
      )}
    </AccessShell>
  )
}
